import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface FeedCast {
  hash: string;
  text: string;
  authorFid: number;
  authorUsername: string;
  authorDisplayName: string;
  authorPfpUrl?: string;
  timestamp: string;
  reactions: { likesCount: number; recastsCount: number };
  repliesCount: number;
  channel: { id: string; name: string } | null;
}

/**
 * Fetch recent Farcaster casts via get-farcaster-feed action proxy.
 * Used as a fallback for farcaster-new-cast trigger when no live/last-run data.
 *
 * Strategy:
 * - If trigger has authorFids configured, fetch their feed (following)
 * - If trigger has channelUrl, fetch that channel's feed
 * - Otherwise, fetch trending casts
 *
 * Returns items shaped like the trigger's resultSchema:
 *   { triggeredAt, eventType: "cast.created", body: { data: { ...cast } } }
 */
export const farcasterCastFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload } = ctx;

  const authorFids = triggerPayload.authorFids as number[] | undefined;
  const channelUrl = triggerPayload.channelUrl as string | undefined;

  // Build action payload based on trigger config
  const actionPayload: Record<string, unknown> = { limit: 5 };

  if (authorFids && authorFids.length > 0) {
    // Fetch feed for the first configured author
    actionPayload.feedType = "following";
    actionPayload.fid = authorFids[0];
  } else if (channelUrl) {
    // Extract channel ID from URL (e.g., "https://warpcast.com/~/channel/base" → "base")
    const channelId = channelUrl.split("/").pop();
    if (channelId) {
      actionPayload.feedType = "channel";
      actionPayload.channelId = channelId;
    } else {
      actionPayload.feedType = "trending";
    }
  } else {
    actionPayload.feedType = "trending";
  }

  const response = await authenticatedFetch<{
    code: number;
    data: { result: { casts: FeedCast[]; nextCursor: string | null }; success: boolean };
  }>(`${V2_ENDPOINT}/v1/action-proxy/get-farcaster-feed/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload: actionPayload }),
  });

  if (!response.ok) return [];
  const data = await response.json();
  if (data.code !== 0 || !data.data?.success) return [];

  const casts = data.data.result?.casts ?? [];
  const uniqueAuthorFids: number[] = Array.from(new Set(casts.map((cast: FeedCast) => cast.authorFid)));

  const authorPfps = new Map<number, string>();

  for (const fid of uniqueAuthorFids) {
    try {
      const userResponse = await authenticatedFetch<{
        code: number;
        data: { result: { fid?: number; pfpUrl?: string }; success: boolean };
      }>(`${V2_ENDPOINT}/v1/action-proxy/get-farcaster-user/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payload: { fid } }),
      });

      if (!userResponse.ok) continue;
      const userData = await userResponse.json();
      if (userData.code !== 0 || !userData.data?.success) continue;

      const pfpUrl = userData.data.result?.pfpUrl;
      if (pfpUrl) authorPfps.set(fid, pfpUrl);
    } catch {
      // Best-effort enrichment for picker avatars; the cast list still works without it.
    }
  }

  // Filter by textPattern if configured
  const textPattern = triggerPayload.textPattern as string | undefined;
  let filtered = casts;
  if (textPattern) {
    try {
      const textPatternRegex = new RegExp(textPattern, "i");
      filtered = casts.filter((cast: FeedCast) => textPatternRegex.test(cast.text));
    } catch {
      filtered = casts;
    }
  }

  // Transform to match the trigger's resultSchema format
  return filtered.map((cast: FeedCast) => {
    const pfpUrl = cast.authorPfpUrl || authorPfps.get(cast.authorFid) || "";
    return {
      triggeredAt: cast.timestamp || new Date().toISOString(),
      eventType: "cast.created",
      body: {
        data: {
          hash: cast.hash,
          text: cast.text,
          timestamp: cast.timestamp,
          author: {
            fid: cast.authorFid,
            username: cast.authorUsername,
            display_name: cast.authorDisplayName,
            pfp_url: pfpUrl,
          },
          reactions: cast.reactions,
          replies: { count: cast.repliesCount },
          channel: cast.channel,
        },
      },
      // Metadata for UI display
      _authorUsername: cast.authorUsername,
      _authorDisplayName: cast.authorDisplayName,
      _authorPfpUrl: pfpUrl,
      _text: cast.text,
      _likesCount: cast.reactions?.likesCount ?? 0,
      _recastsCount: cast.reactions?.recastsCount ?? 0,
    };
  });
};
